"use client"

import ImageComponent from "@/components/ui/ImageComponent"
import { addToast, Button, Input } from "@heroui/react"
import { useFormik } from "formik";
import { TbPhoneCall } from "react-icons/tb";
import type { ReactNode } from "react";
import * as Yup from "yup";
import { usePostScheduleCallBackAPIMutation } from "@/redux/rtkQueries/clientSideGetApis";

// import { MdKeyboardArrowDown } from "react-icons/md";
// import { useGetAllStoreCityAPIQuery } from "@/redux/rtkQueries/clientSideGetApis";

const getCallBackSchema = Yup.object({
    fullName: Yup.string()
        .trim()
        .required("Full name is required")
        .min(2, "Name must be at least 2 characters"),
    email: Yup.string()
        .trim()
        .test(
            "optional-email",
            "Enter a valid email address",
            (value) => !value || Yup.string().email().isValidSync(value)
        ),
    phone: Yup.string()
        .trim()
        .required("Phone number is required")
        .test(
            "phone-digits",
            "Enter a valid phone number (at least 10 digits)",
            (value) => {
                if (!value) return false;
                const digits = value.replace(/\D/g, "");
                return digits.length >= 10 && digits.length <= 15;
            }
        ),
});


interface GetCallBackIndexProps {
    mobilePromotionalBanner?: { url: string; alt?: string };
    laptopPromotionalBanner?: { url: string; alt?: string };
    treatmentSlug:string
    /** Server-rendered offer banner (e.g. `<GetCallBackOfferBanner slug={...} />` from a Server Component parent). */
    children?: ReactNode;
}

const GetCallBackIndex = ({ mobilePromotionalBanner, laptopPromotionalBanner,treatmentSlug, children }: GetCallBackIndexProps) => {

    const [postScheduleCallback] = usePostScheduleCallBackAPIMutation();

    const formik = useFormik({
        initialValues: {
            fullName: "",
            email: "",
            phone: "",
        },
        validationSchema: getCallBackSchema,
        onSubmit: async (values, { resetForm, setSubmitting }) => {
            const formData = new FormData();
            formData.append("fullName", values.fullName.trim());
            formData.append("email", values.email.trim());
            formData.append("phone", values.phone.trim());
            formData.append('treatment',treatmentSlug);
            try {
                const res = await postScheduleCallback(formData).unwrap();
                addToast({
                    title: res.message ?? "Thanks — we will call you back soon.",
                    color: "success",
                    timeout: 3000,
                });
                resetForm();
            } catch {
                // Errors are surfaced via `baseQueryWithAuth` (toast) + `unwrap()` rejection
            } finally {
                setSubmitting(false);
            }
        },
    });

    const laptopBannerUrl = laptopPromotionalBanner?.url ?? '/images/hair_removal/hairRemoval_banner.svg';
    const mobileBannerUrl = mobilePromotionalBanner?.url || null;
    const bannerAlt = laptopPromotionalBanner?.alt ?? mobilePromotionalBanner?.alt ?? 'ad_banner';

    return (
        <div className="space-y-5">
            <div
                id="treatment-hero-banner"
                className="h-full relative min-h-fit"
            >
                <ImageComponent url={laptopBannerUrl} mobile_url={mobileBannerUrl} img_title={bannerAlt} object_cover={true} quality={100} unoptimized imgPriority={true} fullWidthNaturalHeight width={1920} height={450}/>

                <div className="hidden absolute top-0 left-1/2 -translate-x-1/2 z-10 w-full pr-4 lg:flex justify-end h-full">
                    <div className="w-[27svw] h-full flex flex-col gap-2">
                        {children}
                        <form
                            className="flex flex-col justify-between h-full bg-silverBlue rounded-lg py-2 xl:py-5 px-4 xl:px-8 gap-1"
                            onSubmit={formik.handleSubmit}
                            noValidate
                        >
                            <Input
                                type="text"
                                name="fullName"
                                variant="underlined"
                                placeholder="Full Name"
                                value={formik.values.fullName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={Boolean(formik.touched.fullName && formik.errors.fullName)}
                                errorMessage={formik.touched.fullName ? formik.errors.fullName : undefined}
                                classNames={{
                                    inputWrapper: ['call_back_input'],
                                    input: ['text-white!'],
                                    errorMessage: ['text-red-200 text-xs'],
                                }}
                            />
                            <Input
                                type="tel"
                                name="phone"
                                variant="underlined"
                                placeholder="Phone Number"
                                autoComplete="tel"
                                value={formik.values.phone}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={Boolean(formik.touched.phone && formik.errors.phone)}
                                errorMessage={formik.touched.phone ? formik.errors.phone : undefined}
                                classNames={{
                                    inputWrapper: ['call_back_input'],
                                    input: ['text-white!'],
                                    errorMessage: ['text-red-200 text-xs'],
                                }}
                            />
                            <Input
                                type="email"
                                name="email"
                                variant="underlined"
                                placeholder="Email Address (optional)"
                                autoComplete="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={Boolean(formik.touched.email && formik.errors.email)}
                                errorMessage={formik.touched.email ? formik.errors.email : undefined}
                                classNames={{
                                    inputWrapper: ['call_back_input'],
                                    input: ['text-white!'],
                                    errorMessage: ['text-red-200 text-xs'],
                                }}
                            />
                            {/* <Dropdown>
                                <DropdownTrigger>
                                    <Button className='rounded-none bg-transparent text-white! capitalize  w-full text-[15px]! font-normal! flex justify-between px-1 border-b-1 border-white' endContent={<MdKeyboardArrowDown className='text-base! text-white font-normal!' />} >
                                        {selectedCity === "all" ? "Select City" : selectedCity}
                                    </Button>
                                </DropdownTrigger>
                                <DropdownMenu
                                    className="w-full"
                                    aria-label="Select city"
                                    items={CityList.map((row) => ({
                                        key: row.cityName,
                                        label: row.cityName,
                                    }))}
                                    selectionMode="single"
                                    selectedKeys={[selectedCity]}
                                    onSelectionChange={(keys) => {
                                        const key = Array.from(keys as Set<string>)[0];
                                        if (key) setSelectedCity(key);
                                    }}
                                >
                                    {(item) => <DropdownItem key={item.key}>{item.label}</DropdownItem>}
                                </DropdownMenu>
                            </Dropdown> */}
                            <Button
                                type="submit"
                                isLoading={formik.isSubmitting}
                                className='rounded-[10px] md:rounded-full btn_white capitalize text-xs! lg:text-sm! 2xl:text-base! font-normal! flex gap-[5px] w-11/12 mx-auto mt-2 min-h-[16px]! py-1 xl:py-2! h-fit!'
                                startContent={!formik.isSubmitting ? <TbPhoneCall className='text-sm 2xl:text-lg' /> : undefined}
                            >
                                Call Me Back
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
            {/*<div className="block lg:hidden w-11/12 md:w-[70%] bg-silverBlue rounded-3xl py-5 px-[7px] mx-auto">
                <form className="space-y-6">
                    <Input
                        type="text"
                        name="text"
                        variant="underlined"
                        placeholder="Full Name"
                        classNames={{
                            inputWrapper: ['call_back_input'],
                            input: ['text-white!'],
                        }}
                    />
                    <Input
                        type="text"
                        name="text"
                        variant="underlined"
                        placeholder="Email Address"
                        classNames={{
                            inputWrapper: ['call_back_input'],
                            input: ['text-white!'],
                        }}
                    />
                    <Input
                        type="text"
                        name="text"
                        variant="underlined"
                        placeholder="Phone Number"
                        classNames={{
                            inputWrapper: ['call_back_input'],
                            input: ['text-white!'],
                        }}
                    />
                    <Dropdown>
                        <DropdownTrigger>
                            <Button className='rounded-none bg-transparent text-white! capitalize  w-full text-[15px]! font-normal! flex justify-between px-1 border-b-1 border-white' endContent={<MdKeyboardArrowDown className='text-base! text-white font-normal!' />} >
                                Select City
                            </Button>
                        </DropdownTrigger>
                        <DropdownMenu className="w-full">
                            <DropdownItem key="all">All</DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                    <Button className='rounded-[10px] md:rounded-full btn_white capitalize py-6.5! text-lg! font-normal! flex gap-[5px] w-11/12 mx-auto mt-4!' startContent={<TbPhoneCall className='text-lg' />} > Get a Callback</Button>
                </form>
            </div>*/}
        </div>
    )
}

export default GetCallBackIndex